// SettingsPanel.jsx
import React, { useEffect, useMemo, useRef } from "react";
import styles from "./styles.module.css";

/* Close on outside click (used for popover mode) */
function useOutsideClick(ref, onOutside) {
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onOutside?.();
        };
        document.addEventListener("mousedown", handler, true);
        return () => document.removeEventListener("mousedown", handler, true);
    }, [ref, onOutside]);
}

/* Close on ESC */
function useEscapeToClose(onClose) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [onClose]);
}

export default function SettingsPanel({
    isAdmin = false,
    anchorRef,
    onClose,
    onLogout,          // () => Promise|void
    onManageUsers,     // optional
    onManageCameras,   // optional
    onManageTags,
}) {
    const panelRef = useRef(null);
    useEscapeToClose(onClose);

    // Position for popover mode (non-admin) to match FilterPanel
    const position = useMemo(() => {
        const btn = anchorRef?.current;
        const rect = btn ? btn.getBoundingClientRect() : { right: 0, bottom: 0, width: 0 };
        const margin = 12;
        const vw = window.innerWidth;
        const desiredW = 360;
        const w = Math.min(Math.max(desiredW, rect.width), Math.max(320, vw - margin * 2));
        const left = Math.min(vw - margin - w, Math.max(margin, rect.right - w));
        const top = rect.bottom + 8;
        return { top, left, width: w };
    }, [anchorRef, isAdmin]);

    // Close on outside click for popover mode only
    useOutsideClick(panelRef, onClose);

    const handleUsers = () => {
        if (onManageUsers) onManageUsers();
        else {
            try { window.dispatchEvent(new CustomEvent("open-manage-users", { detail: { at: Date.now() } })); } catch { }
        }
        onClose?.();
    };

    const handleCameras = () => {
        if (onManageCameras) onManageCameras();
        else {
            try { window.dispatchEvent(new CustomEvent("open-manage-cameras", { detail: { at: Date.now() } })); } catch { }
        }
        onClose?.();
    };

    const handleTags = () => {
        if (onManageTags) onManageTags();
        else {
            try { window.dispatchEvent(new CustomEvent("open-manage-tags", { detail: { at: Date.now() } })); } catch { }
        }
        onClose?.();
    };

    const handleLogout = async () => {
        if (!window.confirm('Are you sure you want to logout?')) return;
        try {
            await onLogout?.();
        } finally {
            onClose?.();
        }
    };

    // Shared logout button style: full width minus small horizontal padding
    const fullWidthStyle = {
        display: "block",
        width: "100%",
    };

    /* ---------------- Admin: centered dialog with backdrop ---------------- */
    if (isAdmin) {
        return (
            <>
                <div
                    ref={panelRef}
                    className={styles.filterPanel}
                    role="dialog"
                    onClick={onClose}
                    aria-label="Settings"
                    style={{
                        position: "fixed",
                        top: position.top,
                        left: position.left,
                        width: position.width,
                        maxWidth: "92vw",
                        zIndex: 1068,
                    }}
                >
                    <div className={styles.filterPanelHeader}>
                        <div className={styles.filterTitle}>Settings</div>
                        <button type="button" className={styles.xBtn} onClick={onClose} aria-label="Close settings">×</button>
                    </div>
                    <div className={styles.filterSection}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            style={fullWidthStyle}
                            onClick={handleUsers}>
                            Manage users</button>
                    </div>
                    <div className={styles.filterSection}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            style={fullWidthStyle}
                            onClick={handleCameras}>
                            Manage cameras</button>
                    </div>
                    <div className={styles.filterSection}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            style={fullWidthStyle}
                            onClick={handleTags}>
                            Manage tags</button>
                    </div>
                    <div className={styles.filterSection}>
                        <button
                            type="button"
                            className="btn btn-danger"
                            style={fullWidthStyle}
                            onClick={handleLogout}
                        >
                            Log out
                        </button>
                    </div>
                </div>
            </>
        );
    }

    /* ---------------- Non-admin: compact anchored popover ---------------- */
    return (
        <div
            ref={panelRef}
            className={styles.filterPanel}
            role="dialog"
            aria-label="Settings"
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                width: position.width,
                maxWidth: "92vw",
                zIndex: 1068,
            }}
        >
            <div className={styles.filterPanelHeader}>
                <div className={styles.filterTitle}>Settings</div>
                <button type="button" className={styles.xBtn} onClick={onClose} aria-label="Close settings">×</button>
            </div>

            <div className={styles.filterSection} style={{ paddingTop: 4 }}>
                <button
                    type="button"
                    className="btn btn-danger"
                    style={fullWidthStyle}
                    onClick={handleLogout}
                >
                    Log out
                </button>
            </div>
        </div>
    );
}
